import { getFormErrorMessage } from "../../../../../lib/forms/form-errors";
import { User, UserRound } from "lucide-react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { KioskTextField } from "../../../../../components/kiosk-field";
import { KioskDateOfBirthField } from "../../../../../components/kiosk-date-of-birth-field";
import { KioskStatePanel } from "../../../../../components/kiosk-state-panel";
import { CHECKIN_PATIENT_LOOKUP_FIRSTNAME_ID } from "./check-in-patient-lookup-model";
import type { CheckInPatientLookupFormApi } from "./use-check-in-patient-lookup";

/**
 * Error text for a lookup field: shown once the patient left the field or
 * tried to search, never while they are still typing the first characters.
 */
function visibleFieldError(
  meta: { readonly isBlurred: boolean; readonly errors: readonly unknown[] },
  submitted: boolean,
): unknown {
  return (meta.isBlurred || submitted) && meta.errors.length > 0
    ? meta.errors[0]
    : undefined;
}

/**
 * Renders the manual check-in patient lookup fields. The search itself is the
 * step's footer action. The date format is a persistent hint below the date
 * field (KC-70); the first-name field appears with a body-size explanation
 * when the name is ambiguous (KC-69).
 *
 * @since 1.0.0
 * @example
 * ```ts
 * type CheckInPatientLookupFormProps = Parameters<typeof CheckInPatientLookupForm>[0];
 * ```
 */
export function CheckInPatientLookupForm({
  form,
  isSearching,
  needsFirstname,
  onSubmit,
}: {
  form: CheckInPatientLookupFormApi;
  isSearching: boolean;
  needsFirstname: boolean;
  /** Submits the search (Enter in a field). */
  onSubmit: () => void;
}) {
  const { t } = useTranslation();

  useEffect(
    function focusFirstnameWhenRequested() {
      if (needsFirstname) {
        document.getElementById(CHECKIN_PATIENT_LOOKUP_FIRSTNAME_ID)?.focus();
      }
    },
    [needsFirstname],
  );

  return (
    <form
      data-testid="frontdesk-patient-lookup-form"
      className="flex flex-col gap-6"
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit();
      }}
    >
      <form.Subscribe selector={(state) => state.submissionAttempts > 0}>
        {(submitted) => (
          <>
            <form.Field name="lastname">
              {(field) => {
                const error = visibleFieldError(field.state.meta, submitted);
                return (
                  <KioskTextField
                    id="checkin-lookup-lastname"
                    testId="frontdesk-patient-lookup-lastname-input"
                    label={t("self-service.check-in.patient-lookup.lastname")}
                    icon={UserRound}
                    required
                    autoComplete="family-name"
                    value={field.state.value}
                    onChange={field.handleChange}
                    onBlur={field.handleBlur}
                    disabled={isSearching}
                    error={error === undefined ? undefined : t(getFormErrorMessage(error))}
                  />
                );
              }}
            </form.Field>

            <form.Field name="dob">
              {(field) => {
                const error = visibleFieldError(field.state.meta, submitted);
                return (
                  <KioskDateOfBirthField
                    id="checkin-lookup-dob"
                    testId="frontdesk-patient-lookup-dob-input"
                    pickerTestId="frontdesk-patient-lookup-dob-picker-btn"
                    label={t("self-service.check-in.patient-lookup.dob")}
                    description={t("self-service.check-in.dob-format-hint")}
                    required
                    value={field.state.value}
                    onChange={field.handleChange}
                    onBlur={field.handleBlur}
                    disabled={isSearching}
                    error={error === undefined ? undefined : t(getFormErrorMessage(error))}
                  />
                );
              }}
            </form.Field>
          </>
        )}
      </form.Subscribe>

      {needsFirstname ? (
        <>
          <KioskStatePanel
            tone="info"
            title={t("self-service.check-in.patient-lookup.ambiguous-firstname-v2")}
            testId="frontdesk-patient-lookup-ambiguous-notice"
          />
          <form.Field name="firstname">
            {(field) => (
              <KioskTextField
                id={CHECKIN_PATIENT_LOOKUP_FIRSTNAME_ID}
                testId="frontdesk-patient-lookup-firstname-input"
                label={t("self-service.check-in.patient-lookup.firstname")}
                icon={User}
                required
                autoComplete="given-name"
                value={field.state.value || ""}
                onChange={field.handleChange}
                onBlur={field.handleBlur}
                disabled={isSearching}
              />
            )}
          </form.Field>
        </>
      ) : null}

      {/* Enter in a field submits; the visible search action is in the footer. */}
      <button type="submit" hidden tabIndex={-1} aria-hidden />
    </form>
  );
}
